<script lang="ts">
  import type { SuggestionKeyDownProps } from "@tiptap/suggestion";
  import type { Block, BlockGroup } from "./suggestion";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import cx from "classnames";
  import Separator from "$components/+blocks/Separator/Separator.svelte";

  export let items: BlockGroup[];

  let position = { block: 0, i: 0 };
  let container: HTMLDivElement;

  const selectItem = (item: Block) => item.command($$props as any);

  /**
   * External onKeyDown handler for the suggestion extension (see suggestion onKeyDown)
   */
  export const onKeyDown = ({ event }: SuggestionKeyDownProps) => {
    if (event.key === "ArrowUp") {
      let block = position.block;
      let i = position.i - 1;

      // check if we crossed back into another group
      if (i === -1) {
        // we jumped groups, reset to last item of previous block
        // possibly negative so add length
        block = (position.block - 1 + items.length) % items.length;
        i = items[block].blocks.length - 1;
      }
      position = { block, i };

      // scroll container to keep active item in view
      const item = container.querySelector(`[data-item="${block}_${i}"]`) as HTMLElement;
      if (item) item.scrollIntoView({ block: "nearest" });
      return true;
    }

    if (event.key === "ArrowDown") {
      const i = (position.i + 1) % items[position.block].blocks.length;

      // if rolled around, select next block
      const block = i === 0 ? (position.block + 1) % items.length : position.block;

      position = { block, i };

      // scroll container to keep active item in view
      const item = container.querySelector(`[data-item="${block}_${i}"]`) as HTMLElement;
      if (item) item.scrollIntoView({ block: "nearest" });
      return true;
    }

    if (event.key === "Enter") {
      const item = items[position.block].blocks[position.i];
      if (item) {
        selectItem(item);
        event.stopPropagation();
        return true;
      }
    }

    // not a valid key, don't capture
    return false;
  };
</script>

<div
  id="suggestion"
  class="glossy-effect relative h-full min-w-[220px] overflow-hidden rounded-lg border border-gray-300 bg-white drop-shadow-lg dark:border-white/10"
>
  <div
    class="scrollbar-thin h-full max-h-[400px] space-y-4 overflow-y-auto px-1 py-3"
    bind:this={container}
  >
    {#each items as group, group_index}
      {#if group_index !== 0}
        <Separator />
      {/if}

      <div class="px-2">
        {#if group.blocks.length}
          <div class="px-2 pb-2 text-sm font-medium text-gray-400 dark:text-gray-400/70">
            {group.title}
          </div>
        {/if}

        {#each group.blocks as item, index}
          <button
            class={`block w-full rounded py-2 text-left hover:bg-black/8 dark:hover:bg-white/8 ${
              group_index === position.block &&
              index === position.i &&
              "active bg-black/5 dark:bg-white/5"
            }`}
            on:click={() => selectItem(item)}
            on:mouseover={() => (position = { block: group_index, i: index })}
            on:focus={() => (position = { block: group_index, i: index })}
            data-item="{group_index}_{index}"
          >
            <div class="flex items-center">
              <div
                class={cx(
                  "inline-flex items-center justify-center rounded-lg pl-2 pr-3 text-center text-2xl",
                  group_index === position.block && index === position.i
                    ? "border-gray-400/70 text-gray-500 dark:border-white/30 dark:text-gray-200" // current position
                    : "border-gray-300 text-gray-400 dark:border-white/10 "
                )}
              >
                <OVIcon icon={item.icon} />
              </div>

              <div class="mr-2 flex-none space-y-1">
                <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {item.title}
                </p>
                {#if item.desc}
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {item.desc}
                  </p>
                {/if}
              </div>
            </div>
          </button>
        {/each}
      </div>
    {/each}
  </div>
</div>
